<script>
  import { createEventDispatcher } from "svelte";
  import { slide, fade } from "svelte/transition";
  import { updateJob, deleteJob, activateJobLive } from "../../data.js";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import Edit from "carbon-icons-svelte/lib/Edit.svelte";
  import PlayFilled from "carbon-icons-svelte/lib/PlayFilled.svelte";
  import Save from "carbon-icons-svelte/lib/Save.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import Location from "carbon-icons-svelte/lib/Location.svelte";
  import Enterprise from "carbon-icons-svelte/lib/Enterprise.svelte";
  import User from "carbon-icons-svelte/lib/User.svelte";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import Email from "carbon-icons-svelte/lib/Email.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import Layers from "carbon-icons-svelte/lib/Layers.svelte";
  import Radio from "carbon-icons-svelte/lib/Radio.svelte";
  import Document from "carbon-icons-svelte/lib/Document.svelte";
  import Map from "carbon-icons-svelte/lib/Map.svelte";
  import CenterCircle from "carbon-icons-svelte/lib/CenterCircle.svelte";
  import Renew from "carbon-icons-svelte/lib/Renew.svelte";
  import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
  import Add from "carbon-icons-svelte/lib/Add.svelte";
  import Calendar from "carbon-icons-svelte/lib/Calendar.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import PhoneInput from "../../components/PhoneInput.svelte";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";
  import { activeTab as appActiveTab } from "../../stores.js";
  import { fetchAndLoadQuote } from "../../quoteUtils.js";
  import { getFullAndRemainderDays } from "../Quotes/fleetScheduleCalculator.js";
  import TurnaroundSchedulePanel from "./TurnaroundSchedulePanel.svelte";

  export let job = null;
  export let map = null;

  const dispatch = createEventDispatcher();

  let isEditMode = false;
  let isSaving = false;
  let saveSuccess = false;
  let saveError = null;
  let activeTab = "overview"; // 'overview' | 'materials' | 'fleet' | 'contact'

  // Delete Job confirmation modal state
  let showDeleteConfirm = false;
  let isDeleting = false;
  let deleteError = null;

  // Editable form state initialized from job
  let formData = {
    id: null,
    __job_number__: "",
    status: "active",
    quote_id: null,
    quote_number: "",
    po_number: "",
    start_date: "",
    days_count: 1,
    start_time: "07:00",
    finish_time: "17:00",
    load_interval: 15,
    load_uhf_channel: "",
    dispose_uhf_channel: "",
    site_notes: "",
    companyName: "",
    street: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    contactPosition: "",
    notes: "",
    materials: []
  };

  $: if (job) {
    initForm(job);
  }

  function initForm(sourceJob) {
    if (!sourceJob) return;
    const cust = (sourceJob.customer && typeof sourceJob.customer === 'object') ? sourceJob.customer : {};
    const addr = sourceJob.address || cust.address || {};
    const mainCont = (sourceJob.contacts && sourceJob.contacts.length > 0) 
      ? (sourceJob.contacts.find(c => c.main) || sourceJob.contacts[0]) 
      : (sourceJob.main_contact || {});

    formData = {
      id: sourceJob.id,
      __job_number__: sourceJob.__job_number__ || `${cust.id || '1'}-${sourceJob.id}`,
      status: sourceJob.status || "active",
      quote_id: sourceJob.quote_id || null,
      quote_number: sourceJob.quote_number || "",
      po_number: sourceJob.po_number || "",
      start_date: sourceJob.start_date ? sourceJob.start_date.split("T")[0] : "",
      days_count: sourceJob.days_count || 1,
      load_interval: sourceJob.load_interval || 15,
      load_uhf_channel: sourceJob.load_uhf_channel || "",
      dispose_uhf_channel: sourceJob.dispose_uhf_channel || "",
      site_notes: sourceJob.site_notes || sourceJob.notes || "",
      companyName: cust.name || (typeof sourceJob.customer === 'string' ? sourceJob.customer : `Job #${sourceJob.id}`),
      street: addr.street || "",
      suburb: addr.suburb || "",
      state: addr.state || "NSW",
      postcode: addr.postcode || "",
      contactName: mainCont.name || "",
      contactPhone: mainCont.phone || "",
      contactEmail: mainCont.email || "",
      contactPosition: mainCont.position || "Site Contact",
      notes: sourceJob.notes || sourceJob.site_notes || "",
      materials: (sourceJob.materials || []).map(m => {
        const truckDayRate = Number(m.fleet?.truck_day_rate != null ? m.fleet.truck_day_rate : 1400);
        const costPerTonne = m.cost_per_tonne != null ? Number(m.cost_per_tonne) : (m.financial_breakdown?.base_disposal_rate ?? 0);
        const unitPrice = m.proposed_unit_price != null ? Number(m.proposed_unit_price) : (m.unit_price != null ? Number(m.unit_price) : (m.financial_breakdown?.effective_rate_per_tonne ?? 0));
        const totalPrice = m.proposed_total_price != null ? Number(m.proposed_total_price) : (m.total_price != null ? Number(m.total_price) : (m.financial_breakdown?.subtotal_ex_gst ?? 0));
        const runsPerDay = Number(m.schedule?.runs_per_day ?? 5.0);
        const totalLoads = Number(m.schedule?.total_loads ?? 1);
        const estimatedDays = Number(m.schedule?.estimated_days ?? 1.0);
        const tolls = Number(m.schedule?.total_tolls ?? 0);

        return {
          id: m.id || m.__job_material_number__,
          __job_material_number__: m.__job_material_number__ || m.job_material_number,
          name: m.name || m.material_name || "Material",
          amount: m.amount || m.total_tonnes || 0,
          status: m.status || "active",
          start_date: m.start_date || (sourceJob.start_date ? sourceJob.start_date.split("T")[0] : ""),
          days_count: m.days_count || sourceJob.days_count || 1,
          load_interval: m.load_interval || sourceJob.load_interval || 15,
          load_uhf_channel: m.load_uhf_channel || sourceJob.load_uhf_channel || "",
          dispose_uhf_channel: m.dispose_uhf_channel || sourceJob.dispose_uhf_channel || "",
          site_notes: m.site_notes || sourceJob.site_notes || sourceJob.notes || "",
          notes: m.notes || sourceJob.notes || sourceJob.site_notes || "",
          cost_per_tonne: costPerTonne,
          total_cost: m.total_cost != null ? Number(m.total_cost) : (m.financial_breakdown?.subtotal_ex_gst ?? null),
          proposed_unit_price: unitPrice,
          unit_price: unitPrice,
          proposed_total_price: totalPrice,
          total_price: totalPrice,
          margin_percent: m.margin_percent != null ? Number(m.margin_percent) : null,
          polyline: m.polyline || null,
          loading_message: m.loading_message || "",
          disposal_message: m.disposal_message || "",
          load_address: m.load_address ? { ...m.load_address } : null,
          disposal_address: m.disposal_address ? { ...m.disposal_address } : (m.yard ? { name: m.yard.name, suburb: m.yard.suburb } : null),
          yard: m.yard ? {
            id: m.yard.id,
            name: m.yard.name,
            suburb: m.yard.suburb,
            is_cheapest: m.yard.is_cheapest ?? m.yard.is_cheapest_rate ?? false
          } : (m.yard_id ? {
            id: m.yard_id,
            name: m.yard_name,
            suburb: m.yard_suburb,
            is_cheapest: Boolean(m.is_cheapest_rate)
          } : null),
          allocated_trucks: m.allocated_trucks ? [...m.allocated_trucks] : [],
          operating_hours: {
            start_time: m.operating_hours?.start_time || "07:00",
            finish_time: m.operating_hours?.finish_time || "17:00",
            load_mins: Number(m.operating_hours?.load_mins ?? 15),
            tip_mins: Number(m.operating_hours?.tip_mins ?? 15)
          },
          fleet: {
            truck_type: m.fleet?.truck_type || (m.fleet?.truck_capacity_tonne === 12 ? "6 wheeler - 12t" : (m.fleet?.truck_capacity_tonne === 15 ? "8 wheeler - 15t" : "10 wheeler - 19t")),
            truck_capacity_tonne: Number(m.fleet?.truck_capacity_tonne ?? 19),
            trailer_type: m.fleet?.trailer_type || (m.fleet?.trailer_capacity_tonne === 0 ? "None" : "8 wheeler - 20t"),
            trailer_capacity_tonne: Number(m.fleet?.trailer_capacity_tonne ?? 19),
            total_capacity_tonne: Number(m.fleet?.total_capacity_tonne ?? 38),
            truck_day_rate: truckDayRate
          },
          schedule: {
            runs_per_day: runsPerDay,
            total_loads: totalLoads,
            allocated_trucks: parseInt(m.schedule?.allocated_trucks, 10) || (m.allocated_trucks?.length || 1),
            estimated_days: estimatedDays,
            days_single_truck: Number(m.schedule?.days_single_truck ?? (runsPerDay > 0 ? (totalLoads / runsPerDay).toFixed(2) : 1)),
            full_days: Number(m.schedule?.full_days ?? Math.floor(estimatedDays)),
            remainder_days: Number(m.schedule?.remainder_days ?? 0),
            total_tolls: tolls
          }
        };
      })
    };

    // Auto-recalculate any materials that don't have total_cost or margin_percent populated yet
    formData.materials.forEach(m => {
      if (m.total_cost == null || m.margin_percent == null || m.total_loads == null || m.estimated_days == null || m.total_price == null) {
        recalculateMaterial(m);
      }
    });

    saveSuccess = false;
    saveError = null;
    showDeleteConfirm = false;
  }

  function recalculateMaterial(mat, triggeredBy = "") {
    if (!mat) return;

    const amount = Math.max(0, parseFloat(mat.amount) || 0);

    // Fleet capacity (default 38t: 19t truck + 19t trailer)
    const truckCap = Number(mat.fleet?.truck_capacity_tonne ?? 19);
    const trailerCap = Number(mat.fleet?.trailer_capacity_tonne ?? 19);
    const totalCap = Number(mat.fleet?.total_capacity_tonne || (truckCap + trailerCap) || 38);
    if (!mat.fleet) mat.fleet = {};
    mat.fleet.truck_capacity_tonne = truckCap;
    mat.fleet.trailer_capacity_tonne = trailerCap;
    mat.fleet.total_capacity_tonne = totalCap;

    // 1. Total loads
    const loads = (amount > 0 && totalCap > 0) ? Math.ceil(amount / totalCap) : Math.max(1, parseInt(mat.total_loads, 10) || 1);
    mat.total_loads = loads;

    // 2. Turnaround & Estimated days
    const runsPerDay = Math.max(0.1, parseFloat(mat.schedule?.runs_per_day || mat.runs_per_day) || 1.0);
    const numTrucks = Math.max(1, (mat.schedule?.allocated_trucks) || (mat.allocated_trucks && mat.allocated_trucks.length) || 1);
    const dailyFleetLoads = runsPerDay * numTrucks;
    const rawDays = dailyFleetLoads > 0 ? (loads / dailyFleetLoads) : 1;
    const split = getFullAndRemainderDays(rawDays);
    const estimatedDays = Number(rawDays.toFixed(1));
    mat.estimated_days = estimatedDays;

    // 3. Client revenue (total_price ex GST & unit_price)
    if (triggeredBy === "total_price") {
      const totPrice = Math.max(0, parseFloat(mat.proposed_total_price != null ? mat.proposed_total_price : mat.total_price) || 0);
      mat.total_price = totPrice;
      mat.proposed_total_price = totPrice;
      if (amount > 0) {
        const uRate = Number((totPrice / amount).toFixed(2));
        mat.unit_price = uRate;
        mat.proposed_unit_price = uRate;
      }
    } else {
      // triggered by unit_price, amount, or initial
      const unitRate = Math.max(0, parseFloat(mat.proposed_unit_price != null ? mat.proposed_unit_price : mat.unit_price) || 0);
      mat.unit_price = unitRate;
      mat.proposed_unit_price = unitRate;
      const totPrice = Math.round(amount * unitRate * 100) / 100;
      mat.total_price = totPrice;
      mat.proposed_total_price = totPrice;
    }

    // 4. Cartage Cost & Operational Costs
    const dayRate = Math.max(0, parseFloat(mat.fleet?.truck_day_rate != null ? mat.fleet.truck_day_rate : 1400) || 0);
    if (!mat.fleet) {
      mat.fleet = {
        truck_type: "10 wheeler - 19t",
        truck_capacity_tonne: 19,
        trailer_type: "8 wheeler - 20t",
        trailer_capacity_tonne: 19,
        total_capacity_tonne: 38,
        truck_day_rate: dayRate
      };
    } else {
      mat.fleet.truck_day_rate = dayRate;
    }

    // Cartage cost using full days and remainder days
    const fullDaysCost = numTrucks * split.fullDays * dayRate;
    const remainderMultiplier = split.fullDays === 0 ? numTrucks : 1;
    const remainderCost = remainderMultiplier * split.remainderDays * dayRate;
    const cartageCost = fullDaysCost + remainderCost;

    // Disposal cost
    const disposalRate = Math.max(0, parseFloat(mat.cost_per_tonne) || 0);
    const disposalCost = amount * disposalRate;

    // Tolls from schedule exclusively
    const tolls = Math.max(0, parseFloat(mat.schedule?.total_tolls) || 0);

    // Total cost
    const totalCost = Math.round((cartageCost + disposalCost + tolls) * 100) / 100;
    mat.total_cost = totalCost;

    // 5. Margin Percent
    const finalRevenue = parseFloat(mat.proposed_total_price || mat.total_price) || 0;
    if (finalRevenue > 0) {
      mat.margin_percent = Number((((finalRevenue - totalCost) / finalRevenue) * 100).toFixed(1));
    } else {
      mat.margin_percent = 0;
    }

    // Update schedule object
    if (!mat.schedule) mat.schedule = {};
    mat.schedule.runs_per_day = runsPerDay;
    mat.schedule.total_loads = loads;
    mat.schedule.estimated_days = estimatedDays;
    mat.schedule.allocated_trucks = numTrucks;
    mat.schedule.days_single_truck = Number((runsPerDay > 0 ? (loads / runsPerDay).toFixed(2) : 1));
    mat.schedule.full_days = split.fullDays;
    mat.schedule.remainder_days = split.remainderDays;
    mat.schedule.total_tolls = tolls;

    // Clean up any legacy flat properties on mat if present
    delete mat.truck_day_rate;
    delete mat.day_rate;
    delete mat.total_tolls;
    delete mat.load_mins;
    delete mat.tip_mins;
    delete mat.runs_per_day;
    delete mat.total_loads;
    delete mat.estimated_days;
    delete mat.start_time;
    delete mat.finish_time;

    // Trigger Svelte reactivity
    formData = { ...formData };
  }

  function addMaterial() {
    const newMat = {
      id: `mat-${Date.now()}`,
      name: "New Material Package",
      amount: 500,
      status: formData.status || "active",
      start_date: formData.start_date || "",
      days_count: parseInt(formData.days_count, 10) || 1,
      load_interval: parseInt(formData.load_interval, 10) || 15,
      load_uhf_channel: formData.load_uhf_channel || "",
      dispose_uhf_channel: formData.dispose_uhf_channel || "",
      site_notes: formData.site_notes || formData.notes || "",
      notes: formData.notes || formData.site_notes || "",
      cost_per_tonne: 0,
      total_cost: 0,
      proposed_unit_price: 35,
      unit_price: 35,
      proposed_total_price: 17500,
      total_price: 17500,
      margin_percent: 0,
      loading_message: "",
      disposal_message: "",
      load_address: { street: formData.street, suburb: formData.suburb, state: formData.state },
      disposal_address: null,
      yard: null,
      allocated_trucks: [],
      operating_hours: {
        start_time: "07:00",
        finish_time: "17:00",
        load_mins: 15,
        tip_mins: 15
      },
      fleet: {
        truck_type: "10 wheeler - 19t",
        truck_capacity_tonne: 19,
        trailer_type: "8 wheeler - 20t",
        trailer_capacity_tonne: 19,
        total_capacity_tonne: 38,
        truck_day_rate: 1400
      },
      schedule: {
        runs_per_day: 5,
        total_loads: 14,
        allocated_trucks: 1,
        estimated_days: 2.8,
        total_tolls: 0
      }
    };
    recalculateMaterial(newMat);
    formData.materials = [...formData.materials, newMat];
  }

  function removeMaterial(idx) {
    formData.materials = formData.materials.filter((_, i) => i !== idx);
  }

  async function handleDeleteJob() {
    isDeleting = true;
    deleteError = null;
    try {
      const jobId = job.id || job.__job_number__;
      await deleteJob(jobId);
      showDeleteConfirm = false;
      dispatch("job-deleted", { id: jobId });
      dispatch("close");
    } catch (err) {
      console.error("Failed to delete job:", err);
      deleteError = err.message || "Failed to delete job.";
    } finally {
      isDeleting = false;
    }
  }

  function formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString();
  }

  function getStatusTheme(status) {
    switch ((status || "").toLowerCase()) {
      case "active":
        return { label: "Active", bg: "#e6f4ea", color: "#137333", border: "#a8dab5" };
      case "completed":
        return { label: "Completed", bg: "#e8f0fe", color: "#1a73e8", border: "#aecbfa" };
      case "cancelled":
        return { label: "Cancelled", bg: "#fce8e6", color: "#c5221f", border: "#f5c2c7" };
      default:
        return { label: "Proposed", bg: "#fef7e0", color: "#b06000", border: "#fde293" };
    }
  }

  function getTotalTrucks() {
    if (!formData.materials) return 0;
    return formData.materials.reduce((acc, m) => acc + (m.allocated_trucks ? m.allocated_trucks.length : 0), 0);
  }

  function getTotalTonnes() {
    if (!formData.materials) return 0;
    return formData.materials.reduce((acc, m) => acc + (parseFloat(m.amount) || 0), 0);
  }

  function getTotalPrice() {
    if (!formData.materials) return 0;
    return formData.materials.reduce((acc, m) => acc + (parseFloat(m.proposed_total_price || m.total_price) || 0), 0);
  }

  function getTotalCost() {
    if (!formData.materials) return 0;
    return formData.materials.reduce((acc, m) => acc + (parseFloat(m.total_cost) || 0), 0);
  }

  function getTotalMarginPercent() {
    const rev = getTotalPrice();
    const cost = getTotalCost();
    if (rev <= 0) return 0;
    return Number((((rev - cost) / rev) * 100).toFixed(1));
  }

  function focusOnMap() {
    if (!map || !job) return;

    const coords = [];

    // Collect site coordinates
    const clientForMap = job.customer;
    if (clientForMap && clientForMap.address && clientForMap.address.coordinates) {
      const c = clientForMap.address.coordinates;
      if (c.lat && c.lng) coords.push([c.lng, c.lat]);
    }

    // Collect material load/disposal coordinates
    if (job.materials) {
      job.materials.forEach(m => {
        if (m.load_address && m.load_address.coordinates) {
          const lc = m.load_address.coordinates;
          if (lc.lat && lc.lng) coords.push([lc.lng, lc.lat]);
        }
        if (m.disposal_address && m.disposal_address.coordinates) {
          const dc = m.disposal_address.coordinates;
          if (dc.lat && dc.lng) coords.push([dc.lng, dc.lat]);
        }
        if (m.allocated_trucks) {
          m.allocated_trucks.forEach(t => {
            if (t.current_coords && t.current_coords.lat && t.current_coords.lng) {
              coords.push([t.current_coords.lng, t.current_coords.lat]);
            }
          });
        }
      });
    }

    if (coords.length === 0) return;

    if (coords.length === 1) {
      map.flyTo({ center: coords[0], zoom: 14, duration: 1000 });
      return;
    }

    let minLng = coords[0][0], maxLng = coords[0][0];
    let minLat = coords[0][1], maxLat = coords[0][1];

    for (let i = 1; i < coords.length; i++) {
      minLng = Math.min(minLng, coords[i][0]);
      maxLng = Math.max(maxLng, coords[i][0]);
      minLat = Math.min(minLat, coords[i][1]);
      maxLat = Math.max(maxLat, coords[i][1]);
    }

    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
      padding: { top: 70, bottom: 70, left: 70, right: 380 },
      maxZoom: 15,
      duration: 1200
    });
  }

  function focusOnTruck(truck) {
    if (!map || !truck || !truck.current_coords) return;
    const { lat, lng } = truck.current_coords;
    if (lat && lng) {
      map.flyTo({ center: [lng, lat], zoom: 15, duration: 1000 });
    }
  }

  async function handleSave() {
    isSaving = true;
    saveError = null;
    saveSuccess = false;

    try {
      const rawCust = (job.customer && typeof job.customer === 'object') ? job.customer : {};
      const clientObj = {
        ...rawCust,
        name: formData.companyName,
        phone: (rawCust.phone || formData.contactPhone) ? toE164(rawCust.phone || formData.contactPhone) : "",
        address: {
          ...(((job.customer && typeof job.customer === 'object' && job.customer.address) || job.address) || {}),
          street: formData.street,
          suburb: formData.suburb,
          state: formData.state,
          postcode: formData.postcode
        }
      };

      // Build updated job object
      const updatedJob = {
        ...job,
        status: formData.status,
        po_number: formData.po_number,
        start_date: formData.start_date,
        days_count: parseInt(formData.days_count, 10) || 1,
        load_interval: parseInt(formData.load_interval, 10) || 15,
        load_uhf_channel: formData.load_uhf_channel,
        dispose_uhf_channel: formData.dispose_uhf_channel,
        site_notes: formData.site_notes || formData.notes,
        notes: formData.notes || formData.site_notes,
        customer: clientObj,
        address: {
          ...((job.address) || {}),
          street: formData.street,
          suburb: formData.suburb,
          state: formData.state,
          postcode: formData.postcode
        },
        contacts: [
          {
            name: formData.contactName,
            phone: formData.contactPhone ? toE164(formData.contactPhone) : "",
            email: formData.contactEmail,
            position: formData.contactPosition,
            main: true
          }
        ],
        materials: formData.materials.map(m => {
          const orig = (job.materials || []).find(origM => String(origM.id) === String(m.id)) || {};
          const mapped = {
            ...orig,
            ...m,
            amount: parseFloat(m.amount) || 0,
            start_date: m.start_date || formData.start_date || orig.start_date || "",
            days_count: parseInt(m.days_count || formData.days_count || orig.days_count, 10) || 1,
            load_interval: parseInt(m.load_interval || formData.load_interval || orig.load_interval, 10) || 15,
            load_uhf_channel: m.load_uhf_channel || formData.load_uhf_channel || orig.load_uhf_channel || "",
            dispose_uhf_channel: m.dispose_uhf_channel || formData.dispose_uhf_channel || orig.dispose_uhf_channel || "",
            site_notes: m.site_notes || formData.site_notes || formData.notes || orig.site_notes || "",
            notes: m.notes || formData.notes || formData.site_notes || orig.notes || "",
            cost_per_tonne: m.cost_per_tonne != null ? Number(m.cost_per_tonne) : orig.cost_per_tonne,
            total_cost: m.total_cost != null ? Number(m.total_cost) : orig.total_cost,
            proposed_unit_price: m.proposed_unit_price != null ? Number(m.proposed_unit_price) : orig.proposed_unit_price,
            unit_price: m.unit_price != null ? Number(m.unit_price) : (m.proposed_unit_price != null ? Number(m.proposed_unit_price) : orig.unit_price),
            proposed_total_price: m.proposed_total_price != null ? Number(m.proposed_total_price) : orig.proposed_total_price,
            total_price: m.total_price != null ? Number(m.total_price) : (m.proposed_total_price != null ? Number(m.proposed_total_price) : orig.total_price),
            margin_percent: m.margin_percent != null ? Number(m.margin_percent) : orig.margin_percent,
            operating_hours: {
              start_time: m.operating_hours?.start_time || orig.operating_hours?.start_time || "07:00",
              finish_time: m.operating_hours?.finish_time || orig.operating_hours?.finish_time || "17:00",
              load_mins: Number(m.operating_hours?.load_mins ?? orig.operating_hours?.load_mins ?? 15),
              tip_mins: Number(m.operating_hours?.tip_mins ?? orig.operating_hours?.tip_mins ?? 15)
            },
            fleet: {
              ...(orig.fleet || {}),
              ...(m.fleet || {}),
              truck_type: m.fleet?.truck_type ?? orig.fleet?.truck_type ?? "10 wheeler - 19t",
              truck_capacity_tonne: Number(m.fleet?.truck_capacity_tonne ?? orig.fleet?.truck_capacity_tonne ?? 19),
              trailer_type: m.fleet?.trailer_type ?? orig.fleet?.trailer_type ?? "8 wheeler - 20t",
              trailer_capacity_tonne: Number(m.fleet?.trailer_capacity_tonne ?? orig.fleet?.trailer_capacity_tonne ?? 19),
              total_capacity_tonne: Number(m.fleet?.total_capacity_tonne ?? orig.fleet?.total_capacity_tonne ?? 38),
              truck_day_rate: Number(m.fleet?.truck_day_rate ?? orig.fleet?.truck_day_rate ?? 1400)
            },
            schedule: {
              ...(orig.schedule || {}),
              ...(m.schedule || {}),
              runs_per_day: Number(m.schedule?.runs_per_day ?? orig.schedule?.runs_per_day ?? 5.0),
              total_loads: Number(m.schedule?.total_loads ?? orig.schedule?.total_loads ?? 1),
              allocated_trucks: parseInt(m.schedule?.allocated_trucks ?? orig.schedule?.allocated_trucks, 10) || (m.allocated_trucks?.length || 1),
              estimated_days: Number(m.schedule?.estimated_days ?? orig.schedule?.estimated_days ?? 1.0),
              days_single_truck: Number(m.schedule?.days_single_truck ?? orig.schedule?.days_single_truck ?? 1.0),
              full_days: Number(m.schedule?.full_days ?? orig.schedule?.full_days ?? 0),
              remainder_days: Number(m.schedule?.remainder_days ?? orig.schedule?.remainder_days ?? 0),
              total_tolls: Number(m.schedule?.total_tolls ?? orig.schedule?.total_tolls ?? 0)
            },
            polyline: m.polyline || orig.polyline || null,
            load_address: m.load_address || orig.load_address || null,
            disposal_address: m.disposal_address || orig.disposal_address || null,
            yard: (m.yard && typeof m.yard === 'object') ? {
              id: m.yard.id ?? orig.yard?.id ?? orig.yard_id,
              name: m.yard.name ?? orig.yard?.name,
              suburb: m.yard.suburb || m.yard.address?.suburb || orig.yard?.suburb || "",
              image: m.yard.image || orig.yard?.image || null,
              address: m.yard.address || orig.yard?.address || null,
              contacts: m.yard.contacts || orig.yard?.contacts || [],
              main_contact: m.yard.main_contact || orig.yard?.main_contact || null,
              is_cheapest: m.yard.is_cheapest ?? m.yard.is_cheapest_rate ?? false
            } : (m.yard != null ? m.yard : (orig.yard ? orig.yard : null))
          };
          delete mapped.truck_day_rate;
          delete mapped.day_rate;
          delete mapped.total_tolls;
          delete mapped.load_mins;
          delete mapped.tip_mins;
          delete mapped.runs_per_day;
          delete mapped.total_loads;
          delete mapped.estimated_days;
          delete mapped.start_time;
          delete mapped.finish_time;
          delete mapped.target_completion_date;
          delete mapped.geometry;
          if (m.financial_breakdown || orig.financial_breakdown) {
            mapped.financial_breakdown = {
              ...(orig.financial_breakdown || {}),
              ...(m.financial_breakdown || {})
            };
          }
          return mapped;
        })
      };
      delete updatedJob.company;
      delete updatedJob.company_id;
      delete updatedJob.start_time;
      delete updatedJob.finish_time;
      delete updatedJob.target_completion_date;
      delete updatedJob.financial_summary;
      delete updatedJob.customer_summary;

      const result = await updateJob(updatedJob);
      job = { ...updatedJob, ...(result || {}) };

      // If the job is active, ensure it is synced to live tracking
      if (job.status === "active") {
        try {
          const jId = job.id || job._id || job.__job_number__;
          console.log(`[JobEditor] Syncing active job #${jId} to live DB via POST /jobs/live...`);
          await activateJobLive(jId);
        } catch (liveErr) {
          console.error(`[JobEditor] Warning syncing job to live DB:`, liveErr);
        }
      }

      saveSuccess = true;
      dispatch("job-saved", { job });

      setTimeout(() => {
        saveSuccess = false;
        isEditMode = false;
      }, 1500);
    } catch (err) {
      console.error("Failed to save job:", err);
      saveError = err.message || "Failed to save job changes.";
    } finally {
      isSaving = false;
    }
  }

  let isActivating = false;
  let activateSuccessNotice = "";

  async function handleActivateFromDrawer() {
    isActivating = true;
    saveError = null;
    try {
      const jId = formData.id || formData._id || formData.__job_number__;
      console.log(`[JobEditor] Activating job #${jId} to live tracking...`);
      formData.status = "active";
      formData.materials = (formData.materials || []).map(m => ({ ...m, status: "active" }));
      await handleSave();
      activateSuccessNotice = `Job #${formData.__job_number__ || jId} is now active and live!`;
      setTimeout(() => {
        activateSuccessNotice = "";
      }, 4000);
    } catch (err) {
      console.error("[JobEditor] Failed to activate job:", err);
      saveError = err.message || "Failed to activate job.";
    } finally {
      isActivating = false;
    }
  }

  async function handleDrawerDirectStatusChange(event) {
    const newStatus = event.target.value;
    formData.status = newStatus;
    formData.materials = (formData.materials || []).map(m => ({ ...m, status: newStatus }));
    try {
      await handleSave();
    } catch (err) {
      console.error("[JobEditor] Failed to update status:", err);
      saveError = err.message || "Failed to update status.";
    }
  }

  function handleCancelEdit() {
    initForm(job);
    isEditMode = false;
    saveError = null;
  }

  function handleClose() {
    dispatch("close");
  }

  async function handleViewQuote() {
    try {
      const qId = formData.quote_id || formData.quote_number;
      await fetchAndLoadQuote(qId, job);
    } catch (err) {
      console.warn("Could not load quote from JobEditor:", err);
    } finally {
      $appActiveTab = "quotes";
      dispatch("close");
    }
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div class="job-editor-backdrop" role="presentation" on:click={handleClose}>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div 
    class="job-editor-drawer" 
    role="dialog"
    aria-modal="true"
    on:click|stopPropagation
    transition:slide={{ duration: 250, axis: 'x' }}
  >
    <!-- Header -->
    <header class="drawer-header">
      <div class="header-main">
        <div class="company-badge-row">
          <div class="company-avatar">
            <Enterprise size={20} />
          </div>
          <div class="header-titles">
            <div class="company-name-row">
              <h2 class="company-name" title={formData.companyName}>{formData.companyName}</h2>
              <span class="job-number-tag">#{formData.__job_number__}</span>
            </div>
            <div class="site-location-sub">
              <Location size={13} class="icon-sub" />
              <span>{formData.street ? `${formData.street}, ` : ''}{formData.suburb} {formData.state}</span>
            </div>
          </div>
        </div>

        <div class="header-actions">
          <!-- Focus on Map Button -->
          <button 
            class="header-btn icon-btn" 
            title="Focus map on this job's sites and fleet"
            type="button"
            on:click={focusOnMap}
          >
            <CenterCircle size={18} />
          </button>

          {#if isEditMode}
            <!-- Cancel Button in Header -->
            <button 
              class="header-btn header-cancel-btn" 
              type="button" 
              on:click={handleCancelEdit} 
              disabled={isSaving}
              title="Cancel editing without saving"
            >
              <span>Cancel</span>
            </button>

            <!-- Save Button in Header -->
            <button 
              class="header-btn header-save-btn" 
              type="button" 
              on:click={handleSave} 
              disabled={isSaving}
              title="Save all changes to operations database"
            >
              {#if isSaving}
                <span class="btn-spinner"></span>
                <span>Saving...</span>
              {:else}
                <Save size={16} />
                <span>Save</span>
              {/if}
            </button>
          {:else}
            <!-- Toggle View / Edit Mode -->
            <button 
              class="header-btn mode-toggle-btn"
              type="button"
              on:click={() => { isEditMode = true; saveSuccess = false; }}
              title="Switch to Edit Mode"
            >
              <Edit size={16} />
              <span>Edit</span>
            </button>
          {/if}

          <!-- Delete Job Button -->
          <button 
            class="header-btn icon-btn delete-btn" 
            title="Delete this job"
            type="button"
            on:click={() => showDeleteConfirm = true}
          >
            <TrashCan size={18} />
          </button>

          <!-- Close Drawer Button -->
          <button 
            class="header-btn icon-btn close-btn" 
            title="Close Drawer" 
            type="button"
            on:click={handleClose}
          >
            <Close size={20} />
          </button>
        </div>
      </div>

      <!-- Status and Meta Bar -->
      <div class="status-meta-bar">
        <div class="status-indicator-group">
          <span class="meta-label">STATUS</span>
          {#if isEditMode}
            <select class="status-select" bind:value={formData.status}>
              <option value="active">Active</option>
              <option value="proposed">Proposed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          {:else}
            <!-- Status Dropdown (to the left of Activate button) -->
            <select 
              class="status-select-drawer status-select-{(formData.status || 'proposed').toLowerCase()}" 
              value={formData.status || 'proposed'}
              on:change={handleDrawerDirectStatusChange}
              title="Change Job Status"
              aria-label="Change Job Status"
            >
              <option value="proposed">Proposed</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {#if formData.status !== 'active'}
              <button 
                class="btn-drawer-activate" 
                type="button" 
                title="Activate this job and deploy to live fleet tracking"
                disabled={isActivating}
                on:click={handleActivateFromDrawer}
              >
                {#if isActivating}
                  <span class="btn-spinner-xs"></span>
                  <span>Activating...</span>
                {:else}
                  <PlayFilled size={12} />
                  <span>Activate Job</span>
                {/if}
              </button>
            {/if}
          {/if}
        </div>

        <div class="summary-chips">
          <div class="sum-chip" title="Total Allocated Fleet">
            <DeliveryTruck size={14} />
            <span><strong>{getTotalTrucks()}</strong> trucks</span>
          </div>
          <div class="sum-chip" title="Total Material Haulage Target">
            <Layers size={14} />
            <span><strong>{formatNumber(getTotalTonnes())}</strong> t</span>
          </div>
          {#if getTotalPrice() > 0}
            <div class="sum-chip revenue-chip" title="Total Quoted Haulage Value (Ex GST)">
              <span class="chip-curr">$</span>
              <span><strong>{formatNumber(Math.round(getTotalPrice()))}</strong></span>
            </div>
          {/if}
          {#if getTotalPrice() > 0 && getTotalMarginPercent() !== null}
            <div class="sum-chip margin-chip" title="Estimated Overall Margin %">
              <span>Margin <strong>{getTotalMarginPercent()}%</strong></span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Navigation Tabs -->
      <nav class="drawer-tabs">
        <button 
          class="tab-link {activeTab === 'overview' ? 'active' : ''}"
          type="button"
          on:click={() => activeTab = 'overview'}
        >
          <Enterprise size={15} />
          <span>Job & Site</span>
        </button>
        <button 
          class="tab-link {activeTab === 'materials' ? 'active' : ''}"
          type="button"
          on:click={() => activeTab = 'materials'}
        >
          <Layers size={15} />
          <span>Materials ({formData.materials.length})</span>
        </button>
        <button 
          class="tab-link {activeTab === 'fleet' ? 'active' : ''}"
          type="button"
          on:click={() => activeTab = 'fleet'}
        >
          <DeliveryTruck size={15} />
          <span>Fleet ({getTotalTrucks()})</span>
        </button>
        <button 
          class="tab-link {activeTab === 'contact' ? 'active' : ''}"
          type="button"
          on:click={() => activeTab = 'contact'}
        >
          <User size={15} />
          <span>Site Contact</span>
        </button>
      </nav>
    </header>

    <!-- Drawer Scrollable Body -->
    <main class="drawer-body">
      {#if saveSuccess}
        <div class="alert-banner success-banner" transition:slide>
          <Checkmark size={18} />
          <span>Job updated successfully and saved to operations database.</span>
        </div>
      {/if}

      {#if activateSuccessNotice}
        <div class="alert-banner success-banner" transition:slide>
          <Checkmark size={18} />
          <span>{activateSuccessNotice}</span>
        </div>
      {/if}

      {#if saveError}
        <div class="alert-banner error-banner" transition:slide>
          <Close size={18} />
          <span>{saveError}</span>
        </div>
      {/if}

      <!-- TAB 1: OVERVIEW & SITE -->
      {#if activeTab === 'overview'}
        <div class="tab-pane" transition:fade={{ duration: 150 }}>
          <div class="form-section">
            <div class="section-title-row">
              <Enterprise size={16} class="sec-icon" />
              <h3>Company & Site Identification</h3>
            </div>

            <div class="field-grid">
              <div class="field-col col-12">
                <label class="field-label" for="je-company-name">Company / Client Name</label>
                {#if isEditMode}
                  <input id="je-company-name" type="text" class="form-input" bind:value={formData.companyName} placeholder="Company Name" />
                {:else}
                  <div class="read-value bold-text">{formData.companyName}</div>
                {/if}
              </div>

              <div class="field-col col-8">
                <label class="field-label" for="je-street-addr">Site Address (Street)</label>
                {#if isEditMode}
                  <input id="je-street-addr" type="text" class="form-input" bind:value={formData.street} placeholder="Street Address" />
                {:else}
                  <div class="read-value">{formData.street || "—"}</div>
                {/if}
              </div>

              <div class="field-col col-4">
                <label class="field-label" for="je-suburb-addr">Suburb</label>
                {#if isEditMode}
                  <input id="je-suburb-addr" type="text" class="form-input" bind:value={formData.suburb} placeholder="Suburb" />
                {:else}
                  <div class="read-value">{formData.suburb || "—"}</div>
                {/if}
              </div>

              <div class="field-col col-6">
                <label class="field-label" for="je-state-addr">State</label>
                {#if isEditMode}
                  <input id="je-state-addr" type="text" class="form-input" bind:value={formData.state} placeholder="State" />
                {:else}
                  <div class="read-value">{formData.state || "NSW"}</div>
                {/if}
              </div>

              <div class="field-col col-6">
                <label class="field-label" for="je-postcode-addr">Postcode</label>
                {#if isEditMode}
                  <input id="je-postcode-addr" type="text" class="form-input" bind:value={formData.postcode} placeholder="Postcode" />
                {:else}
                  <div class="read-value">{formData.postcode || "—"}</div>
                {/if}
              </div>
            </div>
          </div>

          <!-- Scheduling & Dispatch Specifications -->
          <div class="form-section">
            <div class="section-title-row">
              <Calendar size={16} class="sec-icon" />
              <h3>Scheduling & Dispatch Parameters</h3>
            </div>

            <div class="field-grid">
              <div class="field-col col-6">
                <label class="field-label" for="job-edit-po">Purchase Order / Ref #</label>
                {#if isEditMode}
                  <input id="job-edit-po" type="text" class="form-input" bind:value={formData.po_number} placeholder="e.g. PO-88219" />
                {:else}
                  <div class="read-value bold-text">{formData.po_number || "—"}</div>
                {/if}
              </div>

              <div class="field-col col-4">
                <label class="field-label" for="job-edit-start-date">Scheduled Start Date</label>
                {#if isEditMode}
                  <input id="job-edit-start-date" type="date" class="form-input" bind:value={formData.start_date} />
                {:else}
                  <div class="read-value">{formData.start_date || "—"}</div>
                {/if}
              </div>

              <div class="field-col col-2">
                <label class="field-label" for="job-edit-days">Duration (Days)</label>
                {#if isEditMode}
                  <input id="job-edit-days" type="number" class="form-input" bind:value={formData.days_count} min="1" max="30" />
                {:else}
                  <div class="read-value">{formData.days_count || 1} day(s)</div>
                {/if}
              </div>

              <div class="field-col col-3">
                <label class="field-label" for="job-edit-start-time">Departure Time</label>
                {#if isEditMode}
                  <input id="job-edit-start-time" type="time" class="form-input" bind:value={formData.start_time} />
                {:else}
                  <div class="read-value">{formData.start_time || "07:00"}</div>
                {/if}
              </div>

              <div class="field-col col-3">
                <label class="field-label" for="job-edit-finish-time">Finish Time</label>
                {#if isEditMode}
                  <input id="job-edit-finish-time" type="time" class="form-input" bind:value={formData.finish_time} />
                {:else}
                  <div class="read-value">{formData.finish_time || "17:00"}</div>
                {/if}
              </div>

              <div class="field-col col-4">
                <label class="field-label" for="job-edit-interval">Interval (mins)</label>
                {#if isEditMode}
                  <input id="job-edit-interval" type="number" class="form-input" bind:value={formData.load_interval} min="5" step="5" />
                {:else}
                  <div class="read-value">{formData.load_interval || 15} mins</div>
                {/if}
              </div>

              <div class="field-col col-4">
                <label class="field-label" for="job-edit-load-uhf">Site Loading UHF Channel</label>
                {#if isEditMode}
                  <input id="job-edit-load-uhf" type="text" class="form-input" bind:value={formData.load_uhf_channel} placeholder="e.g. 29" />
                {:else}
                  <div class="read-value bold-text">{formData.load_uhf_channel ? `UHF ${formData.load_uhf_channel}` : "—"}</div>
                {/if}
              </div>

              <div class="field-col col-4">
                <label class="field-label" for="job-edit-dispose-uhf">Disposal Yard UHF</label>
                {#if isEditMode}
                  <input id="job-edit-dispose-uhf" type="text" class="form-input" bind:value={formData.dispose_uhf_channel} placeholder="e.g. 14" />
                {:else}
                  <div class="read-value bold-text">{formData.dispose_uhf_channel ? `UHF ${formData.dispose_uhf_channel}` : "—"}</div>
                {/if}
              </div>

              {#if formData.quote_number || formData.quote_id}
                <div class="field-col col-12">
                  <div
                    class="quote-lineage-chip clickable"
                    on:click={handleViewQuote}
                    on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleViewQuote(); }}
                    role="button"
                    tabindex="0"
                    title="Open this source quote in Quote tab"
                  >
                    <div class="chip-content">
                      <Document size={14} />
                      <span>Originating Quote: <strong>#{formData.quote_number || formData.quote_id}</strong></span>
                    </div>
                    <span class="view-quote-action">View Quote &rarr;</span>
                  </div>
                </div>
              {/if}
            </div>
          </div>

          <!-- Dispatch & Operational Notes -->
          <div class="form-section">
            <div class="section-title-row">
              <Document size={16} class="sec-icon" />
              <h3>Site & Dispatch Notes</h3>
            </div>
            {#if isEditMode}
              <textarea 
                class="form-textarea" 
                rows="4" 
                bind:value={formData.notes} 
                placeholder="Enter access conditions, safety protocols, PPE requirements, or specific site rules..."
              ></textarea>
            {:else}
              <div class="read-box">
                {formData.notes || "No special site or dispatch notes specified for this job."}
              </div>
            {/if}
          </div>
        </div>
      {/if}

      <!-- TAB 2: MATERIALS -->
      {#if activeTab === 'materials'}
        <div class="tab-pane" transition:fade={{ duration: 150 }}>
          <div class="section-title-row mb-12">
            <div class="title-with-count">
              <Layers size={16} class="sec-icon" />
              <h3>Allocated Material Haulage Packages</h3>
              <span class="count-tag">{formData.materials.length} packages</span>
            </div>
            {#if isEditMode}
              <button class="add-mat-package-btn" type="button" on:click={addMaterial}>
                <Add size={14} />
                <span>Add Material</span>
              </button>
            {/if}
          </div>

          <div class="materials-stack">
            {#each formData.materials as mat, idx (mat.id || idx)}
              {@const profit = Math.round((mat.proposed_total_price || mat.total_price || 0) - (mat.total_cost || 0))}
              {@const mPercent = mat.margin_percent != null ? mat.margin_percent : 0}
              <div class="material-card">
                <div class="mat-card-header">
                  <div class="mat-name-box">
                    <span class="mat-indicator-dot"></span>
                    {#if isEditMode}
                      <input type="text" class="mat-name-input" bind:value={mat.name} placeholder="Material Name" />
                    {:else}
                      <span class="mat-title">{mat.name}</span>
                    {/if}
                  </div>

                  <div class="mat-status-box">
                    {#if isEditMode}
                      <select class="mat-status-select" bind:value={mat.status}>
                        <option value="active">Active</option>
                        <option value="proposed">Proposed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button class="remove-mat-btn" type="button" on:click={() => removeMaterial(idx)} title="Remove this material package">
                        <TrashCan size={14} />
                      </button>
                    {:else}
                      {@const matTheme = getStatusTheme(mat.status)}
                      <span class="mat-status-badge" style="background-color: {matTheme.bg}; color: {matTheme.color};">
                        {matTheme.label}
                      </span>
                    {/if}
                  </div>
                </div>

                <div class="mat-card-body">
                  <!-- Section 1: Turnaround Schedule & Fleet Capacities Panel -->
                  <div class="turnaround-panel-wrapper mb-12">
                    <TurnaroundSchedulePanel
                      bind:material={mat}
                      {isEditMode}
                      on:change={() => recalculateMaterial(mat, 'schedule')}
                    />
                  </div>

                  <!-- Section 2: Commercial & Operational Rates -->
                  <div class="mat-rates-panel">
                    <div class="rates-panel-header">
                      <span class="panel-tag">COMMERCIAL RATES & CHARGES</span>
                      <span class="panel-subtext">{isEditMode ? 'Live calculation active' : 'Base operating parameters'}</span>
                    </div>

                    <div class="rates-grid">
                      <div class="rate-col">
                        <label class="rate-lbl" for="rate-unit-{idx}">Client Quoted Rate ($/t)</label>
                        {#if isEditMode}
                          <div class="input-currency">
                            <span class="curr-symbol">$</span>
                            <input 
                              id="rate-unit-{idx}"
                              type="number" 
                              class="rate-num-input" 
                              bind:value={mat.proposed_unit_price} 
                              on:input={() => recalculateMaterial(mat, 'unit_price')}
                              min="0" 
                              step="0.5" 
                            />
                            <span class="curr-unit">/t</span>
                          </div>
                        {:else}
                          <div class="rate-read-val">${Number(mat.proposed_unit_price || mat.unit_price || 0).toFixed(2)} <span class="val-unit">/t</span></div>
                        {/if}
                      </div>

                      <div class="rate-col">
                        <label class="rate-lbl" for="rate-day-{idx}">Truck Day Rate ($/day)</label>
                        {#if isEditMode}
                          <div class="input-currency">
                            <span class="curr-symbol">$</span>
                            <input 
                              id="rate-day-{idx}"
                              type="number" 
                              class="rate-num-input" 
                              bind:value={mat.fleet.truck_day_rate} 
                              on:input={() => recalculateMaterial(mat, 'truck_day_rate')}
                              min="0" 
                              step="50" 
                            />
                            <span class="curr-unit">/day</span>
                          </div>
                        {:else}
                          <div class="rate-read-val">${formatNumber(mat.fleet?.truck_day_rate || 1400)} <span class="val-unit">/day</span></div>
                        {/if}
                      </div>

                      <div class="rate-col">
                        <label class="rate-lbl" for="rate-disp-{idx}">Disposal Gate Fee ($/t)</label>
                        {#if isEditMode}
                          <div class="input-currency">
                            <span class="curr-symbol">$</span>
                            <input 
                              id="rate-disp-{idx}"
                              type="number" 
                              class="rate-num-input" 
                              bind:value={mat.cost_per_tonne} 
                              on:input={() => recalculateMaterial(mat, 'cost_per_tonne')}
                              min="0" 
                              step="0.5" 
                            />
                            <span class="curr-unit">/t</span>
                          </div>
                        {:else}
                          <div class="rate-read-val">${Number(mat.cost_per_tonne != null ? mat.cost_per_tonne : 0).toFixed(2)} <span class="val-unit">/t</span></div>
                        {/if}
                      </div>
                    </div>
                  </div>

                  <!-- Section 3: Revenue, Cost & Profit Margin Bar -->
                  <div class="financials-summary-strip">
                    <div class="fin-stat">
                      <span class="fin-lbl">TOTAL QUOTED (EX GST)</span>
                      {#if isEditMode}
                        <div class="input-currency">
                          <span class="curr-symbol">$</span>
                          <input 
                            type="number" 
                            class="rate-num-input bold-num" 
                            bind:value={mat.proposed_total_price} 
                            on:input={() => recalculateMaterial(mat, 'total_price')}
                            min="0" 
                            step="100" 
                          />
                        </div>
                      {:else}
                        <span class="fin-val highlight">${formatNumber(Math.round(mat.proposed_total_price || mat.total_price || 0))}</span>
                      {/if}
                    </div>

                    <div class="fin-divider"></div>

                    <div class="fin-stat">
                      <span class="fin-lbl">ESTIMATED TOTAL COST</span>
                      <span class="fin-val">${formatNumber(Math.round(mat.total_cost || 0))}</span>
                    </div>

                    <div class="fin-divider"></div>

                    <div class="fin-stat">
                      <span class="fin-lbl">ESTIMATED GROSS PROFIT</span>
                      <div class="margin-badge {mPercent >= 20 ? 'margin-good' : (mPercent >= 10 ? 'margin-fair' : 'margin-low')}">
                        <span class="margin-pct">{mPercent}%</span>
                        <span class="margin-amt">(${formatNumber(profit)})</span>
                      </div>
                    </div>
                  </div>

                  <!-- Route Locations -->
                  <div class="route-details-box">
                    <div class="route-step">
                      <span class="step-badge load-badge">LOAD</span>
                      <div class="step-info">
                        <span class="step-name">Pickup Site</span>
                        <span class="step-addr">
                          {mat.load_address?.street || "Site Address"}{mat.load_address?.suburb ? `, ${mat.load_address.suburb}` : ""}
                        </span>
                      </div>
                    </div>

                    <div class="route-step">
                      <span class="step-badge dispose-badge">DISPOSAL</span>
                      <div class="step-info">
                        <span class="step-name">{mat.yard?.name || "Disposal Facility"}</span>
                        <span class="step-addr">
                          {mat.disposal_address?.street || mat.yard?.name || "Yard Facility"}{mat.disposal_address?.suburb || mat.yard?.suburb ? `, ${mat.disposal_address?.suburb || mat.yard?.suburb}` : ""}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- UHF & Instructions -->
                  <div class="uhf-instructions-grid">
                    <div class="uhf-cell">
                      <div class="cell-head">
                        <Radio size={13} />
                        <span>Load UHF Channel</span>
                      </div>
                      {#if isEditMode}
                        <input type="text" class="uhf-input" bind:value={mat.load_uhf_channel} placeholder="e.g. 29" />
                      {:else}
                        <span class="uhf-val">{mat.load_uhf_channel || "—"}</span>
                      {/if}
                    </div>

                    <div class="uhf-cell">
                      <div class="cell-head">
                        <Radio size={13} />
                        <span>Disposal UHF</span>
                      </div>
                      {#if isEditMode}
                        <input type="text" class="uhf-input" bind:value={mat.dispose_uhf_channel} placeholder="e.g. 14" />
                      {:else}
                        <span class="uhf-val">{mat.dispose_uhf_channel || "—"}</span>
                      {/if}
                    </div>

                    <div class="field-col col-12 mt-6">
                      <label class="field-label-sm" for="je-mat-loading-{idx}">Loading Driver Message</label>
                      {#if isEditMode}
                        <input id="je-mat-loading-{idx}" type="text" class="form-input-sm" bind:value={mat.loading_message} placeholder="e.g. Check in at weighbridge, follow lane 2" />
                      {:else}
                        <div class="read-text-sm">{mat.loading_message || "Standard loading procedures apply"}</div>
                      {/if}
                    </div>

                    <div class="field-col col-12 mt-4">
                      <label class="field-label-sm" for="je-mat-disposal-{idx}">Disposal Instructions</label>
                      {#if isEditMode}
                        <input id="je-mat-disposal-{idx}" type="text" class="form-input-sm" bind:value={mat.disposal_message} placeholder="e.g. Tipping docket required, clean concrete only" />
                      {:else}
                        <div class="read-text-sm">{mat.disposal_message || "Standard tipping rules apply"}</div>
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- TAB 3: FLEET ALLOCATION -->
      {#if activeTab === 'fleet'}
        <div class="tab-pane" transition:fade={{ duration: 150 }}>
          <div class="section-title-row mb-12">
            <DeliveryTruck size={16} class="sec-icon" />
            <h3>Assigned Haulage Fleet</h3>
            <span class="count-tag">{getTotalTrucks()} active trucks</span>
          </div>

          {#if getTotalTrucks() === 0}
            <div class="empty-fleet-state">
              <DeliveryTruck size={36} />
              <span>No trucks currently assigned to this job.</span>
            </div>
          {:else}
            <div class="trucks-grid">
              {#each formData.materials as mat}
                {#if mat.allocated_trucks && mat.allocated_trucks.length > 0}
                  {#each mat.allocated_trucks as item}
                    {@const truck = item.truck || {}}
                    {@const driver = truck.default_driver || item.driver || {}}
                    {@const coords = item.current_coords || {}}
                    
                    <div class="truck-card">
                      <div class="truck-head">
                        <div class="truck-id-group">
                          <DeliveryTruck size={16} class="truck-icon" />
                          <span class="truck-id">{truck.id || "TRUCK"}</span>
                          <span class="truck-type-tag">{truck.type || "Heavy Vehicle"}</span>
                        </div>
                        <span class="truck-cap-tag">{truck.capacity ? `${truck.capacity}t` : ""}</span>
                      </div>

                      <div class="truck-driver-row">
                        <User size={13} class="driver-icon" />
                        <span class="driver-name">{driver.name || "Assigned Driver"}</span>
                      </div>

                      <div class="truck-mat-assignment">
                        <span class="mat-label">Hauling:</span>
                        <span class="mat-val">{mat.name}</span>
                      </div>

                      {#if coords.lat && coords.lng}
                        <div class="truck-loc-row">
                          <div class="coords-text">
                            GPS: {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
                          </div>
                          <button 
                            class="track-truck-btn" 
                            type="button"
                            title="Center map on this truck"
                            on:click={() => focusOnTruck(item)}
                          >
                            <Location size={12} />
                            <span>Track</span>
                          </button>
                        </div>
                      {/if}
                    </div>
                  {/each}
                {/if}
              {/each}
            </div>
          {/if}
        </div>
      {/if}

      <!-- TAB 4: SITE CONTACT -->
      {#if activeTab === 'contact'}
        <div class="tab-pane" transition:fade={{ duration: 150 }}>
          <div class="form-section">
            <div class="section-title-row">
              <User size={16} class="sec-icon" />
              <h3>Primary Site Contact Person</h3>
            </div>

            <div class="field-grid">
              <div class="field-col col-12">
                <label class="field-label" for="je-contact-name">Contact Full Name</label>
                {#if isEditMode}
                  <input id="je-contact-name" type="text" class="form-input" bind:value={formData.contactName} placeholder="Full Name" />
                {:else}
                  <div class="read-value bold-text">{formData.contactName || "—"}</div>
                {/if}
              </div>

              <div class="field-col col-12">
                <label class="field-label" for="je-contact-position">Role / Position</label>
                {#if isEditMode}
                  <input id="je-contact-position" type="text" class="form-input" bind:value={formData.contactPosition} placeholder="e.g. Site Supervisor" />
                {:else}
                  <div class="read-value">{formData.contactPosition || "Site Representative"}</div>
                {/if}
              </div>

              <div class="field-col col-6">
                <label class="field-label" for="job-editor-contact-phone">Phone Number</label>
                {#if isEditMode}
                  <PhoneInput 
                    id="job-editor-contact-phone"
                    bind:value={formData.contactPhone} 
                    placeholder="0412 345 678" 
                  />
                {:else}
                  {#if formData.contactPhone}
                    <a href="tel:{toE164(formData.contactPhone)}" class="contact-action-link">
                      <Phone size={14} />
                      <span>{formatPhoneNumber(formData.contactPhone)}</span>
                    </a>
                  {:else}
                    <div class="read-value">—</div>
                  {/if}
                {/if}
              </div>

              <div class="field-col col-6">
                <label class="field-label" for="je-contact-email">Email Address</label>
                {#if isEditMode}
                  <input id="je-contact-email" type="email" class="form-input" bind:value={formData.contactEmail} placeholder="Email" />
                {:else}
                  {#if formData.contactEmail}
                    <a href="mailto:{formData.contactEmail}" class="contact-action-link">
                      <Email size={14} />
                      <span>{formData.contactEmail}</span>
                    </a>
                  {:else}
                    <div class="read-value">—</div>
                  {/if}
                {/if}
              </div>
            </div>
          </div>
        </div>
      {/if}
    </main>

    <!-- Footer Actions Bar -->
    <footer class="drawer-footer">
      {#if isEditMode}
        <button class="footer-btn btn-danger" type="button" on:click={() => showDeleteConfirm = true} disabled={isSaving}>
          <TrashCan size={15} />
          <span>Delete Job</span>
        </button>
        <button class="footer-btn btn-secondary" type="button" on:click={handleCancelEdit} disabled={isSaving}>
          Cancel
        </button>
        <button class="footer-btn btn-primary" type="button" on:click={handleSave} disabled={isSaving}>
          {#if isSaving}
            <span class="btn-spinner"></span>
            <span>Saving...</span>
          {:else}
            <Save size={16} />
            <span>Save Job Changes</span>
          {/if}
        </button>
      {:else}
        <button class="footer-btn btn-danger" type="button" on:click={() => showDeleteConfirm = true}>
          <TrashCan size={15} />
          <span>Delete Job</span>
        </button>
        <button class="footer-btn btn-secondary" type="button" on:click={focusOnMap}>
          <CenterCircle size={16} />
          <span>Focus on Map</span>
        </button>
        <button class="footer-btn btn-primary" type="button" on:click={() => isEditMode = true}>
          <Edit size={16} />
          <span>Edit Job Details</span>
        </button>
      {/if}
    </footer>
  </div>

  {#if showDeleteConfirm}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="editor-modal-backdrop" role="presentation" on:click={() => showDeleteConfirm = false} transition:fade={{ duration: 150 }}>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div class="editor-modal-card" role="dialog" aria-modal="true" on:click|stopPropagation transition:slide={{ duration: 200 }}>
        <div class="editor-modal-header danger-header">
          <div class="modal-title-row">
            <WarningFilled size={20} class="danger-icon" />
            <h3>Delete Job Confirmation</h3>
          </div>
          <button class="modal-close-btn" type="button" on:click={() => showDeleteConfirm = false}>
            <Close size={18} />
          </button>
        </div>

        <div class="editor-modal-body">
          <p class="modal-desc">
            Are you sure you want to permanently delete <strong>Job #{formData.__job_number__ || formData.id}</strong>
            {#if formData.companyName}
              for <strong>{formData.companyName}</strong>
            {/if}?
          </p>
          <p class="modal-subdesc">
            This will remove the Job record, associated material lines, and all truck fleet allocations.
          </p>

          {#if deleteError}
            <div class="modal-alert error">
              {deleteError}
            </div>
          {/if}
        </div>

        <div class="editor-modal-footer">
          <button class="footer-btn btn-secondary" type="button" on:click={() => showDeleteConfirm = false} disabled={isDeleting}>
            Cancel
          </button>
          <button class="footer-btn btn-danger" type="button" on:click={handleDeleteJob} disabled={isDeleting}>
            {#if isDeleting}
              <span class="btn-spinner"></span>
              <span>Deleting...</span>
            {:else}
              <TrashCan size={15} />
              <span>Confirm Delete</span>
            {/if}
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .job-editor-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1250;
    background-color: rgba(15, 23, 42, 0.25);
    display: flex;
    justify-content: flex-end;
    pointer-events: auto;
  }

  .job-editor-drawer {
    width: 580px;
    max-width: 95vw;
    height: calc(100% - 2rem);
    margin: 1rem 1rem 1rem auto;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: var(--tt-font, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif);
    animation: slideInRight 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  /* Header Styles */
  .drawer-header {
    background-color: #ffffff;
    border-bottom: 1px solid var(--tt-slate-200);
    padding: 16px 20px 0 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
  }

  .header-main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .company-badge-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .company-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background-color: rgba(37, 99, 235, 0.1);
    color: var(--tt-primary, #2563eb);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .header-titles {
    min-width: 0;
    flex: 1;
  }

  .company-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .company-name {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .job-number-tag {
    font-size: 0.75rem;
    font-weight: 700;
    background-color: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
    padding: 2px 8px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
  }

  .site-location-sub {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :global(.icon-sub) {
    flex-shrink: 0;
    color: var(--tt-slate-400, #94a3b8);
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .header-btn {
    border: 1px solid var(--tt-slate-200);
    background: #ffffff;
    border-radius: 8px;
    padding: 6px 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--tt-slate-700);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .header-btn:hover {
    background-color: var(--tt-slate-50);
    border-color: var(--tt-slate-300);
  }

  .header-btn.icon-btn {
    padding: 6px;
    border-radius: 50%;
  }

  .header-btn.close-btn:hover {
    background-color: #fee2e2;
    color: #b91c1c;
    border-color: #fca5a5;
  }

  .header-save-btn {
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .header-save-btn:hover {
    background-color: #1d4ed8;
    border-color: #1d4ed8;
  }

  .header-cancel-btn {
    background-color: #ffffff;
    color: var(--tt-slate-600);
    border-color: var(--tt-slate-300);
  }

  .header-cancel-btn:hover {
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-800);
  }

  /* Status Bar */
  .status-meta-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: var(--tt-slate-50, #f8fafc);
    border-radius: 8px;
  }

  .status-indicator-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .meta-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: var(--tt-slate-400);
    letter-spacing: 0.05em;
  }

  .status-select {
    font-size: 0.82rem;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-300);
    background: #ffffff;
    color: var(--tt-slate-800);
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .status-select-drawer {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 0.78rem;
    font-weight: 700;
    cursor: pointer;
    outline: none;
    transition: all 0.15s ease;
  }

  .status-select-drawer.status-select-active {
    background-color: #e6f4ea;
    color: #137333;
    border: 1px solid #a8dab5;
  }

  .status-select-drawer.status-select-completed {
    background-color: #e8f0fe;
    color: #1a73e8;
    border: 1px solid #aecbfa;
  }

  .status-select-drawer.status-select-cancelled {
    background-color: #fce8e6;
    color: #c5221f;
    border: 1px solid #f5c2c7;
  }

  .status-select-drawer.status-select-proposed,
  .status-select-drawer.status-select-pending {
    background-color: #fef7e0;
    color: #b06000;
    border: 1px solid #fde293;
  }

  .status-pulse-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  .btn-drawer-activate {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background-color: #16a34a;
    color: #ffffff;
    border: 1px solid #15803d;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 11.5px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(22, 163, 74, 0.25);
    transition: all 0.15s ease;
    margin-left: 4px;
  }

  .btn-drawer-activate:hover {
    background-color: #15803d;
    border-color: #166534;
  }

  .btn-drawer-activate:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .btn-spinner-xs {
    width: 10px;
    height: 10px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .summary-chips {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .sum-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.78rem;
    color: var(--tt-slate-700);
    background-color: #ffffff;
    padding: 3px 8px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-200);
  }

  /* Navigation Tabs */
  .drawer-tabs {
    display: flex;
    gap: 4px;
    border-top: 1px solid var(--tt-slate-100);
    padding-top: 4px;
    overflow-x: auto;
  }

  .tab-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--tt-slate-500);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .tab-link:hover {
    color: var(--tt-slate-800);
  }

  .tab-link.active {
    color: var(--tt-primary, #2563eb);
    border-bottom-color: var(--tt-primary, #2563eb);
  }

  /* Body Content */
  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background-color: #ffffff;
  }

  .alert-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 0.84rem;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .success-banner {
    background-color: #e6f4ea;
    color: #137333;
    border: 1px solid #a8dab5;
  }

  .error-banner {
    background-color: #fce8e6;
    color: #c5221f;
    border: 1px solid #f5c2c7;
  }

  .form-section {
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .section-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
  }

  .section-title-row h3 {
    margin: 0;
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  :global(.sec-icon) {
    color: var(--tt-primary, #2563eb);
  }

  .count-tag {
    font-size: 0.72rem;
    font-weight: 700;
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-600);
    padding: 2px 7px;
    border-radius: 9999px;
    margin-left: auto;
  }

  .field-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .field-col {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .col-12 { width: 100%; }
  .col-8 { width: calc(66.666% - 6px); }
  .col-6 { width: calc(50% - 6px); }
  .col-4 { width: calc(33.333% - 8px); }

  .field-label {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--tt-slate-500);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .field-label-sm {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--tt-slate-500);
  }

  .form-input {
    width: 100%;
    padding: 8px 12px;
    font-size: 0.88rem;
    border: 1px solid var(--tt-slate-300);
    border-radius: 8px;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  .form-input:focus {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  .form-input-sm {
    width: 100%;
    padding: 5px 8px;
    font-size: 0.8rem;
    border: 1px solid var(--tt-slate-300);
    border-radius: 6px;
    outline: none;
    box-sizing: border-box;
  }

  .form-textarea {
    width: 100%;
    padding: 10px 12px;
    font-size: 0.85rem;
    border: 1px solid var(--tt-slate-300);
    border-radius: 8px;
    outline: none;
    resize: vertical;
    box-sizing: border-box;
    font-family: inherit;
  }

  .read-value {
    font-size: 0.88rem;
    color: var(--tt-slate-800);
    padding: 4px 0;
  }

  .read-value.bold-text {
    font-weight: 600;
  }

  .read-box {
    font-size: 0.84rem;
    color: var(--tt-slate-600);
    background-color: var(--tt-slate-50);
    border-radius: 8px;
    padding: 10px 12px;
    line-height: 1.5;
  }

  .read-text-sm {
    font-size: 0.8rem;
    color: var(--tt-slate-700);
    padding: 2px 0;
  }

  .contact-action-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.88rem;
    font-weight: 600;
    color: var(--tt-primary, #2563eb);
    text-decoration: none;
    padding: 4px 0;
  }

  .contact-action-link:hover {
    text-decoration: underline;
  }

  /* Materials Stack */
  .materials-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .material-card {
    border: 1px solid var(--tt-slate-200);
    border-radius: 12px;
    overflow: hidden;
    background-color: #ffffff;
  }

  .mat-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background-color: var(--tt-slate-50);
    border-bottom: 1px solid var(--tt-slate-200);
  }

  .mat-name-box {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .mat-indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--tt-primary, #2563eb);
  }

  .mat-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--tt-slate-900);
  }

  .mat-name-input {
    font-size: 0.9rem;
    font-weight: 700;
    padding: 4px 8px;
    border: 1px solid var(--tt-slate-300);
    border-radius: 6px;
  }

  .mat-status-badge {
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 6px;
  }

  .mat-status-select {
    font-size: 0.78rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-300);
  }

  .mat-card-body {
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* Commercial Rates Panel */
  .mat-rates-panel {
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .rates-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .panel-tag {
    font-size: 0.68rem;
    font-weight: 700;
    color: var(--tt-slate-500);
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .panel-subtext {
    font-size: 0.7rem;
    color: var(--tt-slate-400);
    font-style: italic;
  }

  .rates-grid {
    display: flex;
    gap: 14px;
  }

  .rate-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .rate-lbl {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--tt-slate-600);
  }

  .rate-read-val {
    font-size: 0.98rem;
    font-weight: 700;
    color: var(--tt-slate-800);
    padding: 2px 0;
  }

  .input-currency {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300);
    border-radius: 6px;
    padding: 3px 7px;
    transition: border-color 0.15s ease;
  }

  .input-currency:focus-within {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }

  .curr-symbol {
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--tt-slate-400);
  }

  .curr-unit {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--tt-slate-400);
    white-space: nowrap;
  }

  .rate-num-input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--tt-slate-900);
    background: transparent;
    padding: 0;
  }

  .rate-num-input.bold-num {
    font-size: 1.05rem;
    color: #1e40af;
  }

  /* Financials Summary Strip */
  .financials-summary-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 8px 14px;
    gap: 12px;
  }

  .fin-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .fin-lbl {
    font-size: 0.65rem;
    font-weight: 700;
    color: var(--tt-slate-500);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .fin-val {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  .fin-val.highlight {
    color: #15803d;
    font-size: 1.15rem;
  }

  .fin-divider {
    width: 1px;
    height: 32px;
    background-color: #d1fae5;
  }

  .margin-badge {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 6px;
    font-weight: 700;
  }

  .margin-badge.margin-good {
    background-color: #dcfce7;
    color: #15803d;
    border: 1px solid #86efac;
  }

  .margin-badge.margin-fair {
    background-color: #fef3c7;
    color: #b45309;
    border: 1px solid #fcd34d;
  }

  .margin-badge.margin-low {
    background-color: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fca5a5;
  }

  .margin-pct {
    font-size: 0.98rem;
    font-weight: 800;
  }

  .margin-amt {
    font-size: 0.76rem;
    font-weight: 600;
    opacity: 0.9;
  }

  /* Header summary chips */
  .sum-chip.revenue-chip {
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #047857;
  }

  .chip-curr {
    font-weight: 700;
    color: #059669;
  }

  .sum-chip.margin-chip {
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
  }

  .route-details-box {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: var(--tt-slate-50);
    border-radius: 8px;
    padding: 10px 12px;
  }

  .route-step {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .step-badge {
    font-size: 0.65rem;
    font-weight: 800;
    padding: 2px 6px;
    border-radius: 4px;
    flex-shrink: 0;
    letter-spacing: 0.05em;
  }

  .load-badge {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .dispose-badge {
    background-color: #fef3c7;
    color: #92400e;
  }

  .step-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .step-name {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-slate-400);
  }

  .step-addr {
    font-size: 0.82rem;
    font-weight: 500;
    color: var(--tt-slate-700);
  }

  .uhf-instructions-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .uhf-cell {
    width: calc(50% - 5px);
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: 8px;
    padding: 8px 10px;
  }

  .cell-head {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-slate-500);
    margin-bottom: 4px;
  }

  .uhf-val {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  .uhf-input {
    width: 80px;
    padding: 3px 6px;
    font-size: 0.85rem;
    border: 1px solid var(--tt-slate-300);
    border-radius: 6px;
  }

  /* Fleet Grid */
  .trucks-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .truck-card {
    border: 1px solid var(--tt-slate-200);
    border-radius: 10px;
    padding: 12px;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .truck-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .truck-id-group {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .truck-id {
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--tt-slate-900);
  }

  .truck-type-tag {
    font-size: 0.72rem;
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-600);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .truck-cap-tag {
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--tt-primary, #2563eb);
    background-color: rgba(37, 99, 235, 0.08);
    padding: 2px 7px;
    border-radius: 9999px;
  }

  .truck-driver-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    color: var(--tt-slate-600);
  }

  .truck-mat-assignment {
    font-size: 0.8rem;
    color: var(--tt-slate-500);
  }

  .mat-val {
    font-weight: 600;
    color: var(--tt-slate-800);
  }

  .truck-loc-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1px dashed var(--tt-slate-200);
  }

  .coords-text {
    font-size: 0.72rem;
    color: var(--tt-slate-400);
    font-family: monospace;
  }

  .track-truck-btn {
    border: 1px solid var(--tt-slate-200);
    background: #ffffff;
    border-radius: 6px;
    padding: 3px 8px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--tt-primary, #2563eb);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .track-truck-btn:hover {
    background-color: rgba(37, 99, 235, 0.08);
  }

  .empty-fleet-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: var(--tt-slate-400);
    gap: 10px;
    font-size: 0.88rem;
  }

  /* Footer Actions */
  .drawer-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
    flex-shrink: 0;
  }

  .footer-btn {
    padding: 9px 18px;
    border-radius: 8px;
    font-size: 0.86rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary {
    background: #ffffff;
    border: 1px solid var(--tt-slate-300);
    color: var(--tt-slate-700);
  }

  .btn-secondary:hover {
    background-color: var(--tt-slate-100);
  }

  .btn-primary {
    background: var(--tt-primary, #2563eb);
    border: 1px solid var(--tt-primary, #2563eb);
    color: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .btn-primary:hover {
    background-color: #1d4ed8;
    border-color: #1d4ed8;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .mb-12 { margin-bottom: 12px; }
  .mt-4 { margin-top: 4px; }
  .mt-6 { margin-top: 6px; }

  .btn-danger {
    background-color: #c5221f;
    border: 1px solid #c5221f;
    color: #ffffff;
  }

  .btn-danger:hover {
    background-color: #9f1917;
    border-color: #9f1917;
  }

  .header-btn.delete-btn {
    color: #c5221f;
  }

  .header-btn.delete-btn:hover {
    background-color: #fce8e6;
  }

  .title-with-count {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .add-mat-package-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 4px 9px;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s;
  }

  .add-mat-package-btn:hover {
    background-color: #1d4ed8;
  }

  .remove-mat-btn {
    background: transparent;
    border: none;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 3px;
    border-radius: 4px;
    display: flex;
    align-items: center;
  }

  .remove-mat-btn:hover {
    color: #c5221f;
    background-color: #fce8e6;
  }

  .quote-lineage-chip {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    background-color: rgba(37, 99, 235, 0.08);
    color: var(--tt-primary, #2563eb);
    border: 1px solid rgba(37, 99, 235, 0.2);
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    width: 100%;
    transition: all 0.15s ease-in-out;
  }

  .quote-lineage-chip .chip-content {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .quote-lineage-chip.clickable {
    cursor: pointer;
  }

  .quote-lineage-chip.clickable:hover {
    background-color: rgba(37, 99, 235, 0.15);
    border-color: var(--tt-primary, #2563eb);
  }

  .view-quote-action {
    font-weight: 600;
    font-size: 11px;
    text-decoration: underline;
    margin-left: auto;
  }

  /* Modal Backdrop & Card for JobEditor */
  .editor-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2500;
    padding: 16px;
  }

  .editor-modal-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2);
    width: 440px;
    max-width: 95vw;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .editor-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--tt-slate-200);
  }

  .editor-modal-header.danger-header {
    background-color: #fef2f2;
    border-bottom-color: #fecaca;
  }

  .modal-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .modal-title-row h3 {
    font-size: 14.5px;
    font-weight: 700;
    color: #c5221f;
    margin: 0;
  }

  :global(.danger-icon) {
    color: #c5221f;
  }

  .modal-close-btn {
    background: none;
    border: none;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
  }

  .modal-close-btn:hover {
    color: var(--tt-slate-700);
  }

  .editor-modal-body {
    padding: 16px 18px;
  }

  .modal-desc {
    font-size: 13.5px;
    color: var(--tt-slate-800);
    margin: 0 0 8px 0;
    line-height: 1.4;
  }

  .modal-subdesc {
    font-size: 12px;
    color: var(--tt-slate-500);
    margin: 0;
    line-height: 1.4;
  }

  .modal-alert {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    margin-top: 10px;
  }

  .modal-alert.error {
    background-color: #fce8e6;
    color: #c5221f;
    border: 1px solid #f5c2c7;
  }

  .editor-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
  }
</style>
